<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { v4 as uuid } from 'uuid';
import { debounce } from 'lodash';

const model = defineModel<string>();

interface Props {
  id?: string;
  selected?: boolean;
  labelPosition?: 'top' | 'bottom' | 'left' | 'right';
}

const props = withDefaults(defineProps<Props>(), {
  labelPosition: 'top',
  selected: false,
});

const value = ref<string>('');
const inputId = ref(props.id ?? uuid());
const composedStyles = computed(() => {
  let backgroundColor = '#cccccc';
  if (model.value) {
    backgroundColor = model.value;
  }
  return `background-color: ${backgroundColor}`;
});

// Sync the local value with the model
onMounted(() => {
  value.value = model.value || '';
});

const updateValue = debounce(
  () => {
    model.value = value.value;
  },
  50,
  { leading: true, trailing: true, maxWait: 50 }
);

// Handle input event properly
const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  value.value = target.value;
  updateValue();
};
</script>

<template>
  <div
    class="tw-flex tw-justify-between tw-gap-4 tw-items-center tw-relative"
    :class="{
      'tw-flex-row-reverse': labelPosition === 'left',
      'tw-flex-col-reverse': labelPosition === 'top',
      'tw-flex-col': labelPosition === 'bottom',
      ColorPickerSelected: selected,
    }"
  >
    <label
      :for="inputId"
      :style="composedStyles"
      class="tw-h-10 tw-flex-shrink-0 tw-w-10 tw-rounded-full tw-cursor-pointer tw-border-solid tw-border-connx-grey-tint-85 tw-ring-1 tw-ring-connx-grey-tint-25 tw-flex tw-items-center tw-justify-center focus-within:tw-rounded-none tw-mb-0"
    >
      <input
        :id="inputId"
        class="tw-h-0 tw-w-0 tw-opacity-0 tw-absolute"
        :value="modelValue"
        type="color"
        @input="onInput"
      />
    </label>
    <label class="tw-cursor-pointer" :for="inputId">
      <slot></slot>
    </label>
  </div>
</template>
